// Modal "como esse número foi calculado": a fórmula do KPI instanciada com os números
// reais da linha clicada (fechamento) — fórmula → substituição → resultado + contexto.
import { useEffect } from 'react';
import { KpisJanelas } from '../types';
import { fmtMoney, fmtNum, fmtPct, fmtPp } from '../utils/format';
import { AJUDA } from '../utils/ajudas';

export type TipoKpi = 'ns' | 'prazo' | 'corte';

interface Props {
  tipo: TipoKpi;
  label: string; // nome da linha (seller/indústria/BU/Total)
  kpis: KpisJanelas;
  m1Label?: string;
  onClose: () => void;
}

const TITULOS: Record<TipoKpi, string> = {
  ns: 'NS Quantidade (Fill Rate)',
  prazo: '% no Prazo',
  corte: 'Corte',
};

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="py-2 border-b border-grid last:border-0">
      <div className="text-[10px] font-bold uppercase tracking-[.4px] text-muted mb-0.5">{rotulo}</div>
      <div className="text-sm tabular-nums">{children}</div>
    </div>
  );
}

export default function ExplicaKpi({ tipo, label, kpis, m1Label, onClose }: Props) {
  // ESC fecha (o clique fora já fecha pelo overlay)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mtd = kpis.mtd;
  const m1 = kpis.m1;
  // payload antigo (carregado antes de uma atualização da API) não traz os somatórios crus
  const temNumeros = mtd.qtd_comprada !== undefined;

  const deltaDe = (a: number | null | undefined, b: number | null | undefined) =>
    (a !== null && a !== undefined && b !== null && b !== undefined ? a - b : null);

  let formula: string;
  let substituicao: React.ReactNode;
  let contexto: React.ReactNode;
  let delta: number | null;
  let direcaoBoa: 'up' | 'down' = 'up';

  if (tipo === 'ns') {
    formula = 'NS = unidades entregues ÷ unidades compradas (só pedidos apurados)';
    substituicao = <>{fmtNum(mtd.qtd_entregue)} ÷ {fmtNum(mtd.qtd_comprada)} = <strong>{fmtPct(mtd.ns_qtd)}</strong></>;
    contexto = (
      <>
        {fmtPct(mtd.pct_apurado)} do volume do mês já apurado · {fmtNum(mtd.pedidos_em_aberto)} pedidos
        em aberto (sem nada faturado) ficam fora do cálculo até a primeira remessa.
      </>
    );
    delta = deltaDe(mtd.ns_qtd, m1.ns_qtd);
  } else if (tipo === 'prazo') {
    formula = '% no prazo = pedidos enviados dentro do SLA ÷ pedidos recebidos';
    substituicao = <>{fmtNum(mtd.pedidos_no_prazo)} ÷ {fmtNum(mtd.pedidos_considerados_prazo)} = <strong>{fmtPct(mtd.pct_no_prazo)}</strong></>;
    contexto = (
      <>
        {fmtNum(mtd.pedidos_em_atraso)} pedidos em atraso (P1) no período — inclui os em aberto que já
        estouraram o SLA do seller × região (default de 3 dias corridos quando não configurado).
      </>
    );
    delta = deltaDe(mtd.pct_no_prazo, m1.pct_no_prazo);
  } else {
    formula = 'Corte = valor esperado (preço × qtd entregue) − valor faturado em NF';
    substituicao = <>{fmtMoney(mtd.valor_esperado)} − {fmtMoney(mtd.valor_nf)} = <strong>{fmtMoney(mtd.corte_valor)}</strong></>;
    contexto = (
      <>
        Equivale a <strong>{fmtPct(mtd.pct_corte)}</strong> do valor comprado no período
        ({fmtMoney(mtd.valor_comprado)}). Mesmo conceito da Árvore de Perdas.
      </>
    );
    delta = deltaDe(mtd.pct_corte, m1.pct_corte);
    direcaoBoa = 'down';
  }

  const deltaFavoravel = delta !== null && (direcaoBoa === 'up' ? delta > 0 : delta < 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{TITULOS[tipo]} — {label}</h2>

        <Linha rotulo="Fórmula">{formula}</Linha>
        {temNumeros ? (
          <>
            <Linha rotulo="Com os números do mês corrente (MTD)">{substituicao}</Linha>
            <Linha rotulo="Contexto">{contexto}</Linha>
          </>
        ) : (
          <Linha rotulo="Com os números do mês corrente (MTD)">
            <span className="muted">Recarregue a página para carregar os números detalhados.</span>
          </Linha>
        )}
        <Linha rotulo={`Comparação justa (${m1Label || 'M-1'})`}>
          {tipo === 'corte' ? fmtPct(m1.pct_corte) : fmtPct(tipo === 'ns' ? m1.ns_qtd : m1.pct_no_prazo)} no mês anterior até o mesmo dia
          {delta !== null && (
            <> → <span className={deltaFavoravel ? 'delta-good' : 'delta-bad'}>
              {delta > 0 ? '▲' : '▼'} {fmtPp(Math.abs(delta))} {deltaFavoravel ? '(melhorou)' : '(piorou)'}
            </span></>
          )}
        </Linha>

        <p className="text-[11px] text-muted mt-3 leading-relaxed">{AJUDA[tipo === 'ns' ? 'ns' : tipo === 'prazo' ? 'prazo' : 'corte']}</p>

        <div className="modal-actions">
          <button className="btn secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
